import { axiosInstance as axios, axiosGraphQlRequest } from '../../utils/axiosInstances'
import {
  createAccountQuery,
  deleteAccountQuery,
  creditAccountMutation,
  CancelPlanSubscriptionByPlanVersionMutation,
  CancelPlanSubscriptionByPlanSubscriptionMutation,
  getAccountActivePlansQuery
} from '../modules/queries'
import { createAccount, updateAccount } from './utils/persistence/mutations'
import { getAccounts, getAccountsDevices } from './utils/persistence/queries'
import { subscribeToPlan } from './utils/totogi/mutations'
import { buildGraphQlPayload } from '@/utils/graphql_helpers'

import { buildReadableAllocationsOrBalances } from './utils/unifiedBalances'
import _ from 'lodash'
import moment from 'moment'
import { graphQLUrl, persistenceUrl } from '@/store/common'

export default {
  namespaced: true,
  state: {
    accounts: [],
    accountsDevices: [],
    accountsDevicesCount: 0,
    accountDevicesScannedCount: 0,
    accountsDevicesNextToken: null
  },
  getters: {},
  mutations: {
    setAccounts (state, newValue) {
      state.accounts = newValue
    },
    setAccountsDevices (state, newValue) {
      state.accountsDevices = newValue
    },
    setAccountsDevicesResult (state, newValue) {
      state.accountsDevices = newValue.accounts_devices
      state.accountsDevicesCount = newValue.count
      state.accountDevicesScannedCount = newValue.scannedCount
      state.accountsDevicesNextToken = newValue.nextToken
      console.log('Counts for accounts devices', newValue.count, newValue.scannedCount)
    }
  },
  actions: {
    async delete (context, { accountId }) {
      await axios.post(
        graphQLUrl,
        deleteAccountQuery(
          accountId,
          context.rootState.providerId
        ),
        {
          headers: {
            Authorization: `${context.rootState.idToken}`
          }
        }
      )
    },
    async create (context, { accountId, accountName = '' }) {
      const result = await axios.post(
        graphQLUrl,
        createAccountQuery(
          accountId,
          context.rootState.providerId), {
          headers: {
            Authorization: `${context.rootState.idToken}`
          }
        }
      )
      if (!result?.data?.data?.createAccount?.account) {
        return
      }
      await axios.post(
        persistenceUrl,
        buildGraphQlPayload(createAccount, {
          input: {
            accountId,
            accountName: accountName,
            providerId: context.rootState.providerId
          }
        }),
        context.rootGetters.graphQLHeaders
      )
      await context.dispatch('updateEmulatorPersistence', {
        accountId,
        subscribedPlans: [],
        creditLimit: 0,
        churnScore: 0
      })
    },
    async credit (context, { accountId, amount }) {
      await axios.post(
        graphQLUrl,
        creditAccountMutation(
          accountId,
          context.rootState.providerId,
          amount
        ), {
          headers: {
            Authorization: `${context.rootState.idToken}`
          }
        }
      )
      const account = await context.dispatch('getAccountAndActivePlans', { accountId })
      await context.dispatch('updateEmulatorPersistence', {
        accountId,
        creditLimit: account.creditLimit
      })
      return account.creditLimit
    },
    async getAccountAndActivePlans (context, { accountId }) {
      return (await axios.post(
        graphQLUrl,
        getAccountActivePlansQuery(accountId, context.rootState.providerId),
        context.rootGetters.graphQLHeaders
      )).data.data.getAccount
    },
    async updateEmulatorPersistence (context, { accountId, subscribedPlans = null, creditLimit = null, churnScore = null }) {
      const input = {
        providerId: context.rootState.providerId,
        accountId: accountId
      }
      if (subscribedPlans) {
        input.subscribedPlans = subscribedPlans
      }
      if (creditLimit !== null && creditLimit !== undefined) {
        input.creditLimit = creditLimit
      }
      if (churnScore !== null && churnScore !== undefined) {
        input.churnScore = churnScore
      }
      return await axiosGraphQlRequest(
        persistenceUrl,
        updateAccount,
        {
          input: input
        },
        context.rootGetters.graphQLHeaders
      )
    },
    async loadAccountsDevices (context, options) {
      const payload = { providerId: context.rootState.providerId, limit: 10 }
      if (options && options.accountName && options.accountName.length) {
        payload.accountName = options.accountName
      }
      if (options && options.nextToken) {
        payload.nextToken = options.nextToken
      }
      const result = (await axiosGraphQlRequest(
        persistenceUrl,
        getAccountsDevices,
        payload,
        context.rootGetters.graphQLHeaders
      )).data.data.getAccountsDevices
      const accountIds = []
      result.accounts_devices.forEach(accountDevice => {
        accountDevice.accountIdText = accountDevice.accountId.split('-')[0]
        if (accountDevice.accountIdText.length > 8) {
          accountDevice.accountIdText = accountDevice.accountIdText.substr(0, 16)
        }
        if (accountDevice.deviceId) {
          accountDevice.deviceIdText = accountDevice.deviceId.split('-')[0]
          if (accountDevice.deviceIdText.length > 8) {
            accountDevice.deviceIdText = accountDevice.deviceIdText.substr(0, 16)
          }
        }
        accountDevice.createdAtText = moment.unix(accountDevice.createdAt).format('DD-MMM-YYYY')
        if (accountIds.indexOf(accountDevice.accountId) < 0) {
          accountIds.push(accountDevice.accountId)
        }
      })
      const accountsResult = (await axiosGraphQlRequest(
        persistenceUrl,
        getAccounts,
        { providerId: context.rootState.providerId, accountIds: accountIds },
        context.rootGetters.graphQLHeaders
      )).data.data.getAccounts
      const accountsMap = {}
      accountsResult.accounts.forEach(account => {
        accountsMap[account.accountId] = account
      })
      result.accounts_devices.forEach(accountDevice => {
        const account = accountsMap[accountDevice.accountId]
        if (!account) {
          return
        }
        accountDevice.subscribedPlans = account.subscribedPlans || []
        accountDevice.subscribedPlansText = accountDevice.subscribedPlans.map(plan => plan.planName).join(', ')
        accountDevice.churnScore = account.churnScore || 0
        accountDevice.churnScoreText = `${Math.round((accountDevice.churnScore || 0) * 100)}%`
        accountDevice.creditLimit = account.creditLimit
        accountDevice.accountName = account.accountName
      })
      context.commit('setAccountsDevicesResult', result)
      const accountsToUpdate = {}
      if (options && options.syncAccounts) {
        const retrievedAccountsInfo = {}
        for (const index in context.state.accountsDevices) {
          const accountDevice = context.state.accountsDevices[index]
          retrievedAccountsInfo[accountDevice.accountId] = (retrievedAccountsInfo[accountDevice.accountId] ? retrievedAccountsInfo[accountDevice.accountId] : (await context.dispatch('retrieveAccountPlansAndInfo', { accountId: accountDevice.accountId, includeAllocations: false })))
          const accountPlansAndInfo = retrievedAccountsInfo[accountDevice.accountId]
          let persistenceUpdateRequired = false
          if (!accountsToUpdate[accountDevice.accountId]) {
            persistenceUpdateRequired = ((accountDevice.subscribedPlans && accountDevice.subscribedPlans.map(plan => `${plan.planId}${plan.planName}`).sort().join(',') !== accountPlansAndInfo.accountPlans.map(plan => `${plan.planId}${plan.planName}`).sort().join(',')) ||
              (accountDevice.creditLimit !== accountPlansAndInfo.creditLimit) ||
              (accountDevice.churnScore !== accountPlansAndInfo.churnScore))
          }
          accountDevice.subscribedPlans = accountPlansAndInfo.accountPlans
          accountDevice.subscribedPlansText = accountDevice.subscribedPlans.map(plan => plan.planName).join(', ')
          accountDevice.churnScore = accountPlansAndInfo.churnScore
          accountDevice.churnScoreText = `${Math.round((accountDevice.churnScore || 0) * 100)}%`
          accountDevice.creditLimit = accountPlansAndInfo.creditLimit
          if (persistenceUpdateRequired) {
            accountsToUpdate[accountDevice.accountId] = {
              accountId: accountDevice.accountId,
              providerId: context.rootState.providerId,
              subscribedPlans: accountDevice.subscribedPlans.map(plan => {
                return { planId: plan.planId, planName: plan.planName }
              }),
              creditLimit: accountDevice.creditLimit,
              churnScore: accountDevice.churnScore
            }
          }
        }
        console.log('Accounts to update', accountsToUpdate)
        Object.keys(accountsToUpdate).forEach(accountId => {
          const accountUpdatePayload = accountsToUpdate[accountId]
          context.dispatch('updateEmulatorPersistence', accountUpdatePayload)
        })
        context.commit('setAccountsDevices', [...context.state.accountsDevices])
      }
    },
    async subscribeToCurrentPlanVersion (context, { accountId, account = null, planVersionId = null, creditAccount = false, overrides = [] }) {
      await axiosGraphQlRequest(
        graphQLUrl,
        subscribeToPlan,
        {
          input: {
            accountId,
            providerId: context.rootState.providerId,
            planVersionId: planVersionId || context.rootState.currentPlanVersionId,
            overrides
          }
        },
        context.rootGetters.graphQLHeaders
      )
      const accountPlansAndInfo = await context.dispatch('retrieveAccountPlansAndInfo', { accountId: accountId, includeAllocations: false })
      const accountUpdatePayload = {
        accountId: accountId,
        providerId: context.rootState.providerId,
        subscribedPlans: accountPlansAndInfo.accountPlans.map(plan => {
          return { planId: plan.planId, planName: plan.planName }
        })
      }
      if (account) {
        account.subscribedPlans = accountPlansAndInfo.accountPlans
      }
      if (creditAccount) {
        const creditLimit = await context.dispatch('credit', {
          accountId,
          amount: 1000
        })
        accountUpdatePayload.creditLimit = creditLimit
      }
      context.dispatch('updateEmulatorPersistence', accountUpdatePayload)
      return accountPlansAndInfo.accountPlans
    },
    async latestPlanSubscription (context, { activePlans }) {
      return _.sortBy(activePlans, function (plan) {
        return moment(plan.createdAt)
      }).reverse()[0]
    },
    async invokeCancelPlanSubscription (context, { accountId, planSubscriptionId = null }) {
      const payload = [accountId, context.rootState.providerId]
      const cancellationMutation = planSubscriptionId ? CancelPlanSubscriptionByPlanSubscriptionMutation : CancelPlanSubscriptionByPlanVersionMutation
      if (planSubscriptionId) {
        payload.push(planSubscriptionId)
      }
      return (await axios.post(
        graphQLUrl,
        cancellationMutation(...payload),
        context.rootGetters.graphQLHeaders
      ))
    },
    async cancelPlanSubscription (context, { accountId, planVersionId = null, planSubscriptionIds = [], activePlans = [] }) {
      if (activePlans && activePlans.length && planVersionId) {
        if (!planSubscriptionIds) {
          planSubscriptionIds = []
        }
        activePlans.forEach(activePlan => {
          if (activePlan.planVersionId === planVersionId) {
            planSubscriptionIds.push(activePlan.planSubscriptionId)
          }
        })
      }
      console.log(accountId, planVersionId, planSubscriptionIds, activePlans)
      console.log('Cancelling plan subscriptions', planSubscriptionIds)
      if (planSubscriptionIds && planSubscriptionIds.length) {
        for (const planSubscriptionId of planSubscriptionIds) {
          await context.dispatch('invokeCancelPlanSubscription', { accountId, planSubscriptionId })
        }
      } else {
        await context.dispatch('invokeCancelPlanSubscription', { accountId, planSubscriptionId: null })
      }
    },
    async retrieveAccountPlansAndInfo (context, { accountId = null, account = null, includeAllocations = false }) {
      if (!account) {
        account = await context.dispatch('getAccountAndActivePlans', { accountId })
      }
      if (!context.rootState.allAvailablePlanInformation) {
        context.dispatch('planVersionAllocations/getPlanVersionInformation')
      }
      const accountPlans = (account.activePlanVersions || []).map(planSubscription => {
        console.log('Plan active subscription', planSubscription)
        const planVersion = planSubscription.planVersion
        const plan = context.rootState.allAvailablePlanInformation.filter(plan => plan.planVersionId === planVersion.id)[0]
        const subscriptionEndingAt = (planSubscription.to ? moment(planSubscription.to) : null)
        const planExpired = !subscriptionEndingAt || (moment() > plan.subscriptionEndingAt)
        const daysLeftInPlan = subscriptionEndingAt ? subscriptionEndingAt.diff(moment(), 'days') : null
        console.log('Validity', subscriptionEndingAt, planExpired, daysLeftInPlan)
        return {
          subscriptionStartedAt: moment(planSubscription.from),
          subscriptionEndingAt,
          createdAt: planVersion.createdAt,
          modifiedAt: planVersion.modifiedAt,
          overrides: planSubscription.overrides,
          planVersionName: planVersion.version,
          planName: plan?.planName || 'Plan not in available plans',
          planId: plan?.planId || null,
          planSubscriptionId: planSubscription.planSubscriptionId,
          planVersionId: planVersion.id,
          planVersionAllocation: context.dispatch('planVersionAllocations/getPlanVersionInformation', { planVersionId: planVersion.id }, { root: true }),
          planExpired,
          daysLeftInPlan
        }
      })
      if (!includeAllocations) {
        return { accountPlans, churnScore: account.churnScore, creditLimit: account.creditLimit }
      }
      const allocations = []
      for (const plan of accountPlans) {
        plan.planVersionAllocation = await plan.planVersionAllocation
        console.log('Overrides', plan.overrides)
        if (plan.planVersionAllocation.serviceAllowances) {
          console.log(plan.planVersionAllocation.serviceAllowances)
          if (plan.overrides && plan.planVersionAllocation.serviceNameMap) {
            const serviceNameMap = plan.planVersionAllocation.serviceNameMap
            const overrides = plan.overrides
            const serviceAllowances = plan.planVersionAllocation.serviceAllowances
            for (const serviceAllowance of serviceAllowances) {
              for (const key of Object.keys(serviceAllowance)) {
                if (key && serviceNameMap[key]) {
                  const overrideName = serviceNameMap[key]
                  const override = overrides.filter(overrideObject => overrideObject.name === overrideName)[0]
                  if (override) {
                    let overrideValue = override.value
                    if (isNaN(overrideValue)) {
                      try {
                        overrideValue = Number(JSON.parse(overrideValue)['recurring units'])
                        serviceAllowance[key] = overrideValue
                      } catch (err) {
                        console.error('Error parsing override value', overrideName, overrideValue)
                      }
                    } else {
                      serviceAllowance[key] = Number(overrideValue)
                    }
                  }
                }
              }
            }
          }
          plan.planVersionAllocation.serviceAllowances = await buildReadableAllocationsOrBalances(plan.planVersionAllocation.serviceAllowances, 'allocation')
          plan.planVersionAllocation.serviceAllowances.forEach(allocation => allocations.push(Object.assign({
            planVersionId: plan.planVersionId,
            subscriptionStartedAt: plan.subscriptionStartedAt
          }, allocation)))
        }
      }
      console.log('Built allocations', allocations)
      return { accountPlans, allocations, churnScore: account.churnScore, creditLimit: account.creditLimit }
    }
  }
}
